import React from "react";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import {
  ChoiceConfig,
  DateConfig,
  FieldActor,
  FieldOption,
  FormTemplateField,
  ImageUploadConfig,
  LongTextConfig,
  NumberConfig,
  SectionDescriptionConfig,
  SignatureConfig,
} from "../../../../types/formTemplates";

interface FieldConfigEditorProps {
  field: FormTemplateField;
  onUpdate: (field: FormTemplateField) => void;
}

function newOptionId(existing: FieldOption[]): string {
  let i = existing.length + 1;
  let id = `opt_${i}`;
  const seen = new Set(existing.map((o) => o.id));
  while (seen.has(id)) {
    i += 1;
    id = `opt_${i}`;
  }
  return id;
}

const OptionsEditor: React.FC<{
  options: FieldOption[];
  onChange: (next: FieldOption[]) => void;
}> = ({ options, onChange }) => {
  const atCap = options.length >= 50;

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Options
      </Text>
      {options.map((opt, i) => (
        <Group key={opt.id} gap="xs" wrap="nowrap">
          <TextInput
            size="xs"
            value={opt.label}
            onChange={(e) =>
              onChange(
                options.map((o, j) =>
                  j === i ? { ...o, label: e.currentTarget.value } : o,
                ),
              )
            }
            placeholder={`Option ${i + 1}`}
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => onChange(options.filter((_, j) => j !== i))}
            aria-label="Remove option"
            disabled={options.length <= 1}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        variant="subtle"
        color="var(--brand-teal)"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() =>
          onChange([
            ...options,
            { id: newOptionId(options), label: `Option ${options.length + 1}` },
          ])
        }
        disabled={atCap}
      >
        Add option
      </Button>
      {atCap && (
        <Text size="xs" c="red">
          Maximum of 50 options reached.
        </Text>
      )}
    </Stack>
  );
};

export const FieldConfigEditor: React.FC<FieldConfigEditorProps> = ({
  field,
  onUpdate,
}) => {
  const rawConfig = (field.config ?? {}) as Record<string, unknown>;

  function patchConfig<T extends object>(partial: Partial<T>) {
    onUpdate({
      ...field,
      config: { ...rawConfig, ...partial },
    } as FormTemplateField);
  }

  switch (field.type) {
    case "short_text":
    case "long_text": {
      const cfg: LongTextConfig = {
        placeholder:
          typeof rawConfig.placeholder === "string"
            ? rawConfig.placeholder
            : null,
        maxLength:
          typeof rawConfig.maxLength === "number" ? rawConfig.maxLength : null,
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Group grow>
            <TextInput
              size="xs"
              label="Placeholder"
              value={cfg.placeholder ?? ""}
              onChange={(e) =>
                patchConfig<LongTextConfig>({
                  placeholder: e.currentTarget.value || null,
                })
              }
            />
            <NumberInput
              size="xs"
              label="Max length"
              value={cfg.maxLength ?? ""}
              min={1}
              onChange={(v) =>
                patchConfig<LongTextConfig>({
                  maxLength: typeof v === "number" ? v : null,
                })
              }
            />
          </Group>
        </Stack>
      );
    }
    case "number": {
      const cfg: NumberConfig = {
        min: typeof rawConfig.min === "number" ? rawConfig.min : null,
        max: typeof rawConfig.max === "number" ? rawConfig.max : null,
        decimalPlaces:
          typeof rawConfig.decimalPlaces === "number"
            ? rawConfig.decimalPlaces
            : null,
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Group grow>
            <NumberInput
              size="xs"
              label="Min"
              value={cfg.min ?? ""}
              onChange={(v) =>
                patchConfig<NumberConfig>({
                  min: typeof v === "number" ? v : null,
                })
              }
            />
            <NumberInput
              size="xs"
              label="Max"
              value={cfg.max ?? ""}
              onChange={(v) =>
                patchConfig<NumberConfig>({
                  max: typeof v === "number" ? v : null,
                })
              }
            />
            <NumberInput
              size="xs"
              label="Decimal places"
              min={0}
              max={10}
              value={cfg.decimalPlaces ?? ""}
              onChange={(v) =>
                patchConfig<NumberConfig>({
                  decimalPlaces: typeof v === "number" ? v : null,
                })
              }
            />
          </Group>
        </Stack>
      );
    }
    case "date": {
      const cfg: DateConfig = {
        minDate:
          typeof rawConfig.minDate === "string" ? rawConfig.minDate : null,
        maxDate:
          typeof rawConfig.maxDate === "string" ? rawConfig.maxDate : null,
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Group grow>
            <TextInput
              size="xs"
              label="Minimum date (YYYY-MM-DD)"
              value={cfg.minDate ?? ""}
              onChange={(e) =>
                patchConfig<DateConfig>({
                  minDate: e.currentTarget.value || null,
                })
              }
            />
            <TextInput
              size="xs"
              label="Maximum date (YYYY-MM-DD)"
              value={cfg.maxDate ?? ""}
              onChange={(e) =>
                patchConfig<DateConfig>({
                  maxDate: e.currentTarget.value || null,
                })
              }
            />
          </Group>
        </Stack>
      );
    }
    case "single_choice":
    case "multiple_choice":
    case "dropdown": {
      const cfg: ChoiceConfig = {
        options: Array.isArray(rawConfig.options)
          ? (rawConfig.options as FieldOption[])
          : [{ id: "opt_1", label: "Option 1" }],
      };
      return (
        <Box>
          <Divider variant="dashed" mb="xs" />
          <OptionsEditor
            options={cfg.options}
            onChange={(next) => patchConfig<ChoiceConfig>({ options: next })}
          />
        </Box>
      );
    }
    case "image_upload": {
      const cfg: ImageUploadConfig = {
        maxFiles:
          typeof rawConfig.maxFiles === "number" ? rawConfig.maxFiles : null,
        maxSizeMb:
          typeof rawConfig.maxSizeMb === "number" ? rawConfig.maxSizeMb : null,
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Group grow>
            <NumberInput
              size="xs"
              label="Max files"
              min={1}
              value={cfg.maxFiles ?? ""}
              onChange={(v) =>
                patchConfig<ImageUploadConfig>({
                  maxFiles: typeof v === "number" ? v : null,
                })
              }
            />
            <NumberInput
              size="xs"
              label="Max size (MB)"
              min={1}
              value={cfg.maxSizeMb ?? ""}
              onChange={(v) =>
                patchConfig<ImageUploadConfig>({
                  maxSizeMb: typeof v === "number" ? v : null,
                })
              }
            />
          </Group>
        </Stack>
      );
    }
    case "signature": {
      const cfg: SignatureConfig = {
        signatory: rawConfig.signatory === "staff" ? "staff" : "client",
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Select
            size="xs"
            label="Signatory"
            value={cfg.signatory}
            onChange={(v) =>
              v &&
              patchConfig<SignatureConfig>({
                signatory: v as FieldActor,
              })
            }
            data={[
              { value: "staff", label: "Staff" },
              { value: "client", label: "Client" },
            ]}
          />
        </Stack>
      );
    }
    case "section_description": {
      const cfg: SectionDescriptionConfig = {
        text: typeof rawConfig.text === "string" ? rawConfig.text : "",
      };
      return (
        <Stack gap="xs">
          <Divider variant="dashed" />
          <Textarea
            size="sm"
            label="Description text"
            value={cfg.text ?? ""}
            minRows={3}
            onChange={(e) =>
              patchConfig<SectionDescriptionConfig>({
                text: e.currentTarget.value,
              })
            }
            placeholder="Describe this section for whoever is filling out the form."
          />
        </Stack>
      );
    }
    case "section_header":
    case "divider":
    case "page_break":
      return null;
    default: {
      const _exhaustive: never = field;
      return null;
    }
  }
};
